import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAudienceStore } from "@/lib/stores/audienceStore";
import { useAuthStore } from "@/lib/stores/authStore";
import type { Audience } from "@/lib/types/audience";

export type Theme = "adult" | "child";

interface ThemeContextValue {
  theme: Theme;
  setForcedTheme: (theme: Theme | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const audienceToTheme = (audience: Audience | null): Theme =>
  audience === "CHILD" ? "child" : "adult";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const audience = useAudienceStore((state) => state.audience);
  const { user } = useAuthStore();
  
  const effectiveAudience = user?.audience ?? audience;
  const [forcedTheme, setForcedTheme] = useState<Theme | null>(null);
  const theme = forcedTheme ?? audienceToTheme(effectiveAudience);

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;

    root.setAttribute("data-theme", theme);
    document.body.setAttribute("data-theme", theme);

    console.info("[theme] applied", { theme });
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setForcedTheme,
    }),
    [theme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

export const useForcedTheme = (theme: Theme) => {
  const { setForcedTheme } = useTheme();
  useLayoutEffect(() => {
    setForcedTheme(theme);
    return () => setForcedTheme(null);
  }, [setForcedTheme, theme]);
};
